import { STATUS } from "@/constants/statuses";
import { customerInfoProviderToken, globalLoggerToken } from "@/tokens";
import { applyTransaction, arrayAdd, arrayRemove } from "@datorama/akita";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import { ProductsService } from "@modules/products/services/ProductsService";
import { productsServiceToken } from "@modules/products/tokens";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { HooksManager } from "@modules/sdk/hooks/HooksManager";
import { Observable, Subject, Subscription, from, of } from "rxjs";
import {
    catchError,
    debounceTime,
    delay,
    filter,
    switchMap,
    take,
    tap,
} from "rxjs/operators";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    GetWishlistsParams,
    UpdateWishlistRequestBody,
    WishlistsApiService,
} from "../api_services/WishlistsApiService/WishlistsApiService";
import { WishlistEntity } from "../entities/WishlistEntity";
import { WishlistItemEntity } from "../entities/WishlistItemEntity";
import { WishlistWithItemsEntity } from "../entities/WishlistWithItemsEntity";
import { WishlistProductStatusesQuery } from "../queries/WishlistProductStatusesQuery";
import { WishlistsQuery } from "../queries/WishlistsQuery";
import WishlistsStore from "../stores/WishlistsStore";
import { QueryPaginationMeta, WISHLIST_ITEMS_SORTING_TYPE } from "../types";
import WishlistItemCreatedSdkEvent from "../wishlists_sdk/events/WishlistItemCreatedSdkEvent";
import WishlistItemDeletedSdkEvent from "../wishlists_sdk/events/WishlistItemDeletedSdkEvent";
import {
    WishlistAddItemHookInput,
    WishlistHooksDefinitions,
} from "../wishlists_sdk/hooks/WishlistsSdkHooks";
import { WishlistProductStatusesService } from "./WishlistProductStatusesService";

@singleton()
export class WishlistsService {
    private getItemsTotalCount$ = new Subject<void>();
    private getItemsTotalCountSubscription?: Subscription;

    constructor(
        private readonly wishlistsStore: WishlistsStore,
        private readonly wishlistsQuery: WishlistsQuery,
        private readonly wishlistsApiService: WishlistsApiService,
        @inject(productsServiceToken)
        private readonly productsService: ProductsService,
        private readonly wishlistProductStatusesService: WishlistProductStatusesService,
        private readonly wishlistProductStatusesQuery: WishlistProductStatusesQuery,
        @inject(globalLoggerToken) private readonly logger: LoggerInterface,
        @inject(customerInfoProviderToken)
        private readonly customerInfoProvider: CustomerInfoProviderInterface,
        private readonly hooksManager: HooksManager
    ) {
        this.subscribeToGetItemsTotalCount();
    }

    getWishlists(params?: GetWishlistsParams): Observable<WishlistEntity[]> {
        this.wishlistsStore.setStatus(STATUS.LOADING);
        return this.wishlistsApiService
            .getWishlists({
                orderBy:
                    params?.orderBy ||
                    WISHLIST_ITEMS_SORTING_TYPE.recentlyAdded,
            })
            .pipe(
                tap((v) => {
                    v.forEach((wishlist) => {
                        wishlist.items.forEach((wishlistItem) => {
                            this.productsService.addProductForFetching(
                                wishlistItem.productId
                            );

                            this.wishlistProductStatusesService.addProductIdForFetching(
                                wishlistItem.productId
                            );

                            if (this.customerInfoProvider.getIsB2b()) {
                                this.productsService.addProductWithQuantityRulesForFetching(
                                    wishlistItem.productId
                                );
                            }
                        });
                    });
                }),
                tap((v) => {
                    applyTransaction(() => {
                        this.wishlistsStore.set(v);
                        this.wishlistsStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    getWishlistItems(queryParams: Required<QueryPaginationMeta>) {
        this.logger.debug("WishlistsService.getWishlistItems is started");
        this.wishlistsStore.setLoadMoreStatus(STATUS.LOADING);

        return this.wishlistsApiService.getWishlistItems(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.getWishlistItems is errored",
                    { error }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                    this.wishlistsStore.setLoadMoreStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((data) => {
                data.items.forEach((item) => {
                    this.productsService.addProductForFetching(item.productId);

                    this.wishlistProductStatusesService.addProductIdForFetching(
                        item.productId
                    );
                });
            }),
            tap((data) => {
                this.logger.debug(
                    "WishlistsService.getWishlistItems is ready",
                    { data }
                );

                applyTransaction(() => {
                    this.wishlistsStore.update(
                        queryParams.wishlistId,
                        ({ items }) => ({
                            items: arrayAdd(items, data.items),
                            totalCount: data.totalCount,
                            currentOffset: data.currentOffset,
                        })
                    );
                    this.wishlistsStore.setLoadMoreStatus(STATUS.READY);
                });
            })
        );
    }

    getItemsTotalCount() {
        this.logger.debug("WishlistsService.getItemsTotalCount is started");
        this.getItemsTotalCount$.next();
    }

    subscribeToGetItemsTotalCount() {
        this.getItemsTotalCountSubscription = this.getItemsTotalCount$
            .pipe(
                debounceTime(200),
                switchMap(() =>
                    this.wishlistsApiService.getItemsTotalCount().pipe(
                        catchError((error: unknown) => {
                            this.logger.debug(
                                "WishlistsService.getItemsTotalCount is errored",
                                { error }
                            );
                            throw error;
                        })
                    )
                ),
                tap((data) => {
                    applyTransaction(() => {
                        this.wishlistsStore.update((state) => ({
                            ...state,
                            itemsTotalCount: data.totalCount,
                        }));
                    });
                    this.logger.debug(
                        "WishlistsService.getItemsTotalCount is ready",
                        { data }
                    );
                })
            )
            .subscribe();
    }

    destroy() {
        this.getItemsTotalCountSubscription?.unsubscribe();
    }

    mergeWishlists(saveWishlistsToken: string): Observable<unknown> {
        this.logger.debug("WishlistsService.mergeWishlists is started");

        return this.wishlistsApiService.mergeWishlists(saveWishlistsToken).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.mergeWishlists is errored",
                    { error }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                });
                throw error;
            }),
            tap(() => {
                this.logger.debug("WishlistsService.mergeWishlists is ready", {
                    saveWishlistsToken,
                });
            })
        );
    }

    saveWishlists(
        email: string,
        isAcceptsEmailMarketing: boolean
    ): Observable<unknown> {
        this.wishlistsStore.setSaveStatus(STATUS.LOADING);
        this.logger.debug("WishlistsService.saveWishlists is started");

        return this.wishlistsApiService
            .saveWishlists(email, isAcceptsEmailMarketing)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "WishlistsService.saveWishlists is errored",
                        { error }
                    );
                    applyTransaction(() => {
                        this.wishlistsStore.setError(error);
                        this.wishlistsStore.setSaveStatus(STATUS.ERROR);
                    });
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "WishlistsService.saveWishlists is ready",
                        { email, isAcceptsEmailMarketing }
                    );
                    this.wishlistsStore.setSaveStatus(STATUS.READY);
                }),
                delay(2000),
                tap(() => {
                    this.wishlistsStore.setSaveStatus(STATUS.VIRGIN);
                })
            );
    }

    addItem(data: {
        productId: number;
        variantId: number;
        wishlistId: number | null;
    }): Observable<WishlistItemEntity> {
        const selectWishlist = () => {
            return data.wishlistId
                ? this.wishlistsQuery.selectEntity(data.wishlistId)
                : this.wishlistsQuery.selectFirst();
        };

        const wishlist$ = selectWishlist();

        return wishlist$.pipe(
            tap(() => {
                this.logger.debug("WishlistsService.addItem", data);
                this.wishlistProductStatusesService.setProductStatusLoadingStatus(
                    data.productId
                );
            }),
            switchMap((wishlist) => {
                if (wishlist) {
                    return of(wishlist);
                }
                return this.getWishlists().pipe(
                    switchMap(() => selectWishlist())
                );
            }),
            take(1),
            filter((wishlist): wishlist is WishlistWithItemsEntity => {
                return !!wishlist;
            }),
            switchMap((firstWishlist) => {
                const hookInput: WishlistAddItemHookInput = {
                    productId: data.productId,
                    variantId: data.variantId,
                    wishlistId: firstWishlist.id,
                };

                const addItemHook = this.hooksManager.findHook(
                    WishlistHooksDefinitions.ADD_ITEM
                );

                return from(addItemHook.execute(hookInput)).pipe(
                    switchMap((modifiedData) =>
                        this.wishlistsApiService
                            .addItem({
                                productId: modifiedData.productId,
                                variantId: modifiedData.variantId,
                                wishlistId: modifiedData.wishlistId,
                            })
                            .pipe(
                                tap((addedItem) => {
                                    this.wishlistProductStatusesService.setProductInWishlistStatus(
                                        true,
                                        modifiedData.productId
                                    );
                                    if (modifiedData.variantId) {
                                        this.wishlistProductStatusesService.setVariantInWishlistStatus(
                                            true,
                                            modifiedData.productId,
                                            modifiedData.variantId
                                        );
                                    }
                                    this.wishlistProductStatusesService.changeWishlistItemsCount(
                                        modifiedData.productId,
                                        1
                                    );
                                    this.wishlistProductStatusesService.unsetProductStatusLoadingStatus(
                                        modifiedData.productId
                                    );

                                    if (
                                        modifiedData.productId !==
                                        data.productId
                                    ) {
                                        this.wishlistProductStatusesService.unsetProductStatusLoadingStatus(
                                            data.productId
                                        );
                                    }

                                    this.wishlistsStore.update(
                                        addedItem.wishlistId,
                                        ({ items, totalCount }) => ({
                                            items: arrayAdd(items, addedItem),
                                            totalCount: totalCount + 1,
                                        })
                                    );

                                    this.productsService.addProductForFetching(
                                        modifiedData.productId
                                    );

                                    dispatchSdkEvent(
                                        new WishlistItemCreatedSdkEvent(
                                            addedItem.wishlistId,
                                            addedItem.productId,
                                            addedItem.variantId
                                        )
                                    );
                                    this.getItemsTotalCount();
                                })
                            )
                    )
                );
            })
        );
    }

    deleteItem(data: {
        wishlistId: number | null;
        productId: number;
        variantId: number | null;
    }): Observable<WishlistItemEntity> {
        this.logger.debug("WishlistsService.deleteItem", data);
        this.wishlistProductStatusesService.setProductStatusLoadingStatus(
            data.productId
        );
        return this.wishlistsApiService
            .deleteItem({
                wishlistId: data.wishlistId,
                productId: data.productId,
                variantId: data.variantId,
            })
            .pipe(
                tap((deletedItem) => {
                    applyTransaction(() => {
                        this.wishlistProductStatusesService.unsetProductStatusLoadingStatus(
                            data.productId
                        );
                        this.wishlistProductStatusesService.changeWishlistItemsCount(
                            data.productId,
                            -1
                        );

                        if (data.variantId) {
                            this.wishlistProductStatusesService.setVariantInWishlistStatus(
                                false,
                                data.productId,
                                data.variantId
                            );
                            if (
                                !this.wishlistProductStatusesQuery.getEntity(
                                    data.productId
                                )?.variantsInWishlist.length
                            ) {
                                this.wishlistProductStatusesService.setProductInWishlistStatus(
                                    false,
                                    data.productId
                                );
                            }
                        } else {
                            this.wishlistProductStatusesService.clearVariantInWishlistStatus(
                                data.productId
                            );
                            this.wishlistProductStatusesService.setProductInWishlistStatus(
                                false,
                                data.productId
                            );
                        }

                        this.wishlistsStore.update(
                            data.wishlistId,
                            ({ items, totalCount }) => {
                                const newItems = arrayRemove(
                                    items,
                                    (item: WishlistItemEntity) => {
                                        const productMatches =
                                            item.productId ===
                                            deletedItem.productId;

                                        if (data.variantId === null) {
                                            return productMatches;
                                        }

                                        return (
                                            productMatches &&
                                            item.variantId ===
                                                deletedItem.variantId
                                        );
                                    }
                                );
                                const itemsRemoved = items.length - newItems.length;

                                return {
                                    items: newItems,
                                    totalCount: totalCount - itemsRemoved,
                                };
                            }
                        );

                        dispatchSdkEvent(
                            new WishlistItemDeletedSdkEvent(
                                deletedItem.wishlistId,
                                deletedItem.productId,
                                deletedItem.variantId
                            )
                        );
                        this.getItemsTotalCount();
                    });
                })
            );
    }

    moveItem(data: {
        wishlistId: number;
        productId: number;
        variantId: number | null;
        targetWishlistId: number;
    }) {
        return this.wishlistsApiService
            .moveItem({
                wishlistId: data.wishlistId,
                productId: data.productId,
                variantId: data.variantId,
                targetWishlistId: data.targetWishlistId,
            })
            .pipe(
                tap((movedItem) => {
                    applyTransaction(() => {
                        this.wishlistsStore.update(
                            data.wishlistId,
                            ({ items, totalCount }) => ({
                                items: arrayRemove(
                                    items,
                                    (item: WishlistItemEntity) => {
                                        return (
                                            item.productId ===
                                                movedItem.productId &&
                                            item.variantId ===
                                                movedItem.variantId
                                        );
                                    }
                                ),
                                totalCount: totalCount - 1,
                            })
                        );
                        this.wishlistsStore.update(
                            movedItem.wishlistId,
                            ({ items, totalCount }) => ({
                                items: arrayAdd(items, movedItem),
                                totalCount: totalCount + 1,
                            })
                        );
                    });
                })
            );
    }

    renameWishlist(data: UpdateWishlistRequestBody) {
        return this.wishlistsApiService.updateWishlist(data).pipe(
            tap((updatedWishlist) => {
                applyTransaction(() => {
                    this.wishlistsStore.update(updatedWishlist.id, () => ({
                        title: updatedWishlist.title,
                    }));
                });
            })
        );
    }

    makeWishlistDefault(data: UpdateWishlistRequestBody) {
        return this.wishlistsApiService
            .updateWishlist({ ...data, isDefault: true })
            .pipe(
                tap((defaultWishlist) =>
                    applyTransaction(() => {
                        this.wishlistsStore.update(null, { isDefault: false });
                        this.wishlistsStore.update(defaultWishlist.id, {
                            isDefault: true,
                        });

                        const wishlists = this.wishlistsQuery.getAll();
                        this.wishlistsStore.set([
                            ...wishlists.filter(
                                (wishlist) => wishlist.id === defaultWishlist.id
                            ),
                            ...wishlists.filter(
                                (wishlist) => wishlist.id !== defaultWishlist.id
                            ),
                        ]);
                    })
                )
            );
    }

    togglePrivateWishlist(data: UpdateWishlistRequestBody) {
        return this.wishlistsApiService.updateWishlist(data).pipe(
            tap((updatedWishlist) => {
                applyTransaction(() => {
                    this.wishlistsStore.update(updatedWishlist.id, () => ({
                        isPrivate: updatedWishlist.isPrivate,
                    }));
                });
            })
        );
    }

    deleteWishlist(data: { wishlistId: number }) {
        return this.wishlistsApiService
            .deleteWishlist({
                wishlistId: data.wishlistId,
            })
            .pipe(
                tap((deletedWishlist) => {
                    applyTransaction(() => {
                        const items = this.wishlistsQuery.getEntity(
                            deletedWishlist.id
                        )?.items;

                        if (items) {
                            items.forEach((item) => {
                                this.wishlistProductStatusesService.unsetProductStatusLoadingStatus(
                                    item.productId
                                );
                                this.wishlistProductStatusesService.changeWishlistItemsCount(
                                    item.productId,
                                    -1
                                );
                                this.wishlistProductStatusesService.setProductInWishlistStatus(
                                    false,
                                    item.productId
                                );
                                if (item.variantId) {
                                    this.wishlistProductStatusesService.setVariantInWishlistStatus(
                                        false,
                                        item.productId,
                                        item.variantId
                                    );
                                }
                            });
                        }

                        this.wishlistsStore.remove(deletedWishlist.id);
                    });
                    this.getItemsTotalCount();

                    const remainingWishlists = this.wishlistsQuery.getAll();
                    if (remainingWishlists.length === 0) {
                        this.getWishlists().pipe(take(1)).subscribe();
                    }
                })
            );
    }

    createWishlist(data: { title: string }) {
        return this.wishlistsApiService
            .createWishlist({
                title: data.title,
            })
            .pipe(
                tap((createdWishlist) => {
                    applyTransaction(() => {
                        this.wishlistsStore.add(
                            {
                                ...createdWishlist,
                                items: [],
                                perPage: 20,
                                currentOffset: 0,
                                totalCount: 0,
                            },
                            {
                                prepend: true,
                            }
                        );
                    });
                })
            );
    }

    duplicateWishlist(data: { wishlistId: number }) {
        this.logger.debug("WishlistsService.duplicateWishlist is started");
        return this.wishlistsApiService
            .duplicateWishlist({ wishlistId: data.wishlistId })
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "WishlistsService.duplicateWishlist is errored",
                        { error }
                    );
                    throw error;
                }),
                tap((duplicatedWishlist) => {
                    this.logger.debug(
                        "WishlistsService.duplicateWishlist is ready",
                        duplicatedWishlist
                    );
                    applyTransaction(() => {
                        this.wishlistsStore.add(duplicatedWishlist, {
                            prepend: true,
                        });
                    });
                    this.getItemsTotalCount();
                })
            );
    }

    updateWishlistItemVariant(data: {
        wishlistId: number;
        productId: number;
        oldVariantId: number;
        newVariantId: number;
    }) {
        this.logger.debug(
            "WishlistsService.updateWishlistItemVariant is started"
        );
        return this.wishlistsApiService.updateWishlistItemVariant(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.updateWishlistItemVariant is errored",
                    { error }
                );
                throw error;
            }),
            tap(({ updatedWishlistItem }) => {
                this.logger.debug(
                    "WishlistsService.updateWishlistItemVariant is ready",
                    updatedWishlistItem
                );
                applyTransaction(() => {
                    this.wishlistsStore.update(
                        data.wishlistId,
                        ({ items }) => ({
                            items: items.map((item) =>
                                item.variantId === data.oldVariantId
                                    ? updatedWishlistItem
                                    : item
                            ),
                        })
                    );
                });
            })
        );
    }

    updateWishlistItemQuantity(data: {
        wishlistId: number;
        productId: number;
        variantId: number;
        quantity: number;
    }) {
        this.logger.debug(
            "WishlistsService.updateWishlistItemQuantity is started"
        );
        return this.wishlistsApiService.updateWishlistItemQuantity(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.updateWishlistItemQuantity is errored",
                    { error }
                );
                throw error;
            }),
            tap(({ updatedWishlistItem }) => {
                this.logger.debug(
                    "WishlistsService.updateWishlistItemQuantity is ready",
                    updatedWishlistItem
                );
                applyTransaction(() => {
                    this.wishlistsStore.update(
                        data.wishlistId,
                        ({ items }) => ({
                            items: items.map((item) =>
                                item.productId === data.productId &&
                                item.variantId === data.variantId
                                    ? {
                                          ...item,
                                          quantity:
                                              updatedWishlistItem.quantity,
                                      }
                                    : item
                            ),
                        })
                    );
                });
            })
        );
    }

    createDraftOrder(data: { wishlistId: number }) {
        this.wishlistsStore.setCreateDraftOrderStatus(STATUS.LOADING);
        this.logger.debug("WishlistsService.createDraftOrder is started");

        return this.wishlistsApiService.createDraftOrder(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.createDraftOrder is errored",
                    { error }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                    this.wishlistsStore.setCreateDraftOrderStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap(() => {
                this.logger.debug(
                    "WishlistsService.createDraftOrder is ready",
                    data
                );
                this.wishlistsStore.setCreateDraftOrderStatus(STATUS.READY);
            }),
            delay(4000),
            tap(() => {
                this.wishlistsStore.setCreateDraftOrderStatus(STATUS.VIRGIN);
            })
        );
    }

    addApprover(data: { wishlistId: number; approverId: number }) {
        this.logger.debug("WishlistsService.addApprover is started");
        return this.wishlistsApiService.addApprover(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug("WishlistsService.addApprover is errored", {
                    error,
                });
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                });
                throw error;
            }),
            tap(({ updatedWishlist }) => {
                this.logger.debug("WishlistsService.addApprover is ready", {
                    data,
                });
                applyTransaction(() => {
                    this.wishlistsStore.update(updatedWishlist.id, () => ({
                        approvers: updatedWishlist.approvers,
                    }));
                });
            })
        );
    }

    deleteApprover(data: { wishlistId: number; approverId: number }) {
        this.logger.debug("WishlistsService.deleteApprover is started");
        return this.wishlistsApiService.deleteApprover(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.deleteApprover is errored",
                    {
                        error,
                    }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                });
                throw error;
            }),
            tap(({ updatedWishlist }) => {
                this.logger.debug("WishlistsService.deleteApprover is ready", {
                    data,
                });
                applyTransaction(() => {
                    this.wishlistsStore.update(updatedWishlist.id, () => ({
                        approvers: updatedWishlist.approvers,
                    }));
                });
            })
        );
    }

    approveWishlist(data: { wishlistId: number }) {
        this.wishlistsStore.setApproveWishlistStatus(STATUS.LOADING);
        this.logger.debug("WishlistsService.approveWishlist is started");
        return this.wishlistsApiService.approveWishlist(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.approveWishlist is errored",
                    {
                        error,
                    }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                    this.wishlistsStore.setApproveWishlistStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap(({ updatedWishlist }) => {
                this.logger.debug("WishlistsService.approveWishlist is ready", {
                    data,
                });
                applyTransaction(() => {
                    this.wishlistsStore.update(updatedWishlist.id, () => ({
                        approvers: updatedWishlist.approvers,
                        isApproved: updatedWishlist.isApproved,
                    }));
                    this.wishlistsStore.setApproveWishlistStatus(STATUS.READY);
                });
            })
        );
    }

    sendNotificationToApprovers(data: { wishlistId: number }) {
        this.wishlistsStore.setSendNotificationToApproversStatus(
            STATUS.LOADING
        );
        this.logger.debug(
            "WishlistsService.sendNotificationToApprovers is started"
        );
        return this.wishlistsApiService.sendNotificationToApprovers(data).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "WishlistsService.sendNotificationToApprovers is errored",
                    {
                        error,
                    }
                );
                applyTransaction(() => {
                    this.wishlistsStore.setError(error);
                    this.wishlistsStore.setSendNotificationToApproversStatus(
                        STATUS.ERROR
                    );
                });
                throw error;
            }),
            tap(() => {
                this.logger.debug(
                    "WishlistsService.sendNotificationToApprovers is ready",
                    {
                        data,
                    }
                );
                applyTransaction(() => {
                    this.wishlistsStore.update(data.wishlistId, (wishlist) => ({
                        sentNotificationsCount:
                            (wishlist.sentNotificationsCount || 0) + 1,
                    }));
                    this.wishlistsStore.setSendNotificationToApproversStatus(
                        STATUS.READY
                    );
                });
            })
        );
    }
}
